import { useCallback, useMemo } from "react"

import { TableColumnToggle, type ColumnDef } from "@coval/ui"

interface ColumnInfo {
  key: string
  label: string
  fullLabel?: string
}

interface ColumnSelectorProps {
  columns: ColumnInfo[]
  visibleColumns: Set<string>
  onVisibleColumnsChange: (columns: Set<string>) => void
}

export function ColumnSelector({
  columns,
  visibleColumns,
  onVisibleColumnsChange,
}: ColumnSelectorProps) {
  const toggleColumns = useMemo<ColumnDef<unknown>[]>(
    () =>
      columns.map((column) => ({
        id: column.key,
        header: column.fullLabel || column.label,
        enableReordering: false,
      })),
    [columns],
  )
  const columnVisibility = useMemo(
    () =>
      Object.fromEntries(
        columns.map((column) => [column.key, visibleColumns.has(column.key)]),
      ),
    [columns, visibleColumns],
  )
  const handleColumnVisibilityChange = useCallback(
    (visibility: Record<string, boolean>) => {
      const nextVisibleColumns = new Set(visibleColumns)
      columns.forEach(({ key }) => {
        if (visibility[key] ?? true) {
          nextVisibleColumns.add(key)
        } else {
          nextVisibleColumns.delete(key)
        }
      })
      onVisibleColumnsChange(nextVisibleColumns)
    },
    [columns, onVisibleColumnsChange, visibleColumns],
  )

  return (
    <TableColumnToggle
      inline
      columns={toggleColumns}
      columnVisibility={columnVisibility}
      onColumnVisibilityChange={handleColumnVisibilityChange}
      label="Visible columns"
      showFullLabelOnHover
    />
  )
}
